import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { Code2, ArrowRightLeft, FileSpreadsheet, Phone, LayoutDashboard, ListChecks, CalendarCheck, Wallet, CreditCard, Rocket, ShieldAlert, ArrowRight, BarChart3, Users, Megaphone, Gift, Package, CalendarDays, UserCog, Clock, MessageSquareText, Clock3, FileText, RotateCcw, PackagePlus, Bot, ClipboardList, ChefHat, PenSquare, BadgeCheck, CalendarPlus, MessagesSquare, BadgePercent, Target, TrendingUp, Wand2, Download, Images, ShieldCheck, HeartHandshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { providerApi } from "@/lib/providerApi";
import { Button } from "@/components/ui/button";

export const NAV = [
  { to: "/partner", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/partner/listings", label: "Listings", icon: ListChecks },
  { to: "/partner/bookings", label: "Bookings", icon: CalendarCheck },
  { to: "/partner/new-booking", label: "Add a booking", icon: CalendarPlus },
  { to: "/partner/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/partner/team", label: "Team", icon: UserCog },
  { to: "/partner/rota", label: "Rota & timesheets", icon: Clock },
  { to: "/partner/replies", label: "Quick replies", icon: MessageSquareText },
  { to: "/partner/hours", label: "Opening hours", icon: Clock3 },
  { to: "/partner/invoices", label: "Invoices", icon: FileText },
  { to: "/partner/accounting", label: "Accounting export", icon: FileSpreadsheet },
  { to: "/partner/refunds", label: "Refund requests", icon: RotateCcw },
  { to: "/partner/packages", label: "Packages", icon: PackagePlus },
  { to: "/partner/chats", label: "Customer chats", icon: MessagesSquare },
  { to: "/partner/memberships", label: "Memberships", icon: BadgePercent },
  { to: "/partner/targets", label: "Targets & pay", icon: Target },
  { to: "/partner/growth", label: "Growth", icon: TrendingUp },
  { to: "/partner/listing-helper", label: "Listing helper", icon: Wand2 },
  { to: "/partner/import", label: "Import business", icon: Download },
  { to: "/partner/switch", label: "Switch from another app", icon: ArrowRightLeft },
  { to: "/partner/studio", label: "Image library", icon: Images },
  { to: "/partner/food", label: "Food safety", icon: ShieldCheck },
  { to: "/partner/safety", label: "DBS & safeguarding", icon: HeartHandshake },
  { to: "/partner/assistant", label: "AI assistant", icon: Bot },
  { to: "/partner/forms", label: "Intake forms", icon: ClipboardList },
  { to: "/partner/kitchen", label: "Kitchen", icon: ChefHat },
  { to: "/partner/earnings", label: "Earnings & payouts", icon: Wallet },
  { to: "/partner/payouts", label: "Payouts", icon: Wallet },
  { to: "/partner/developer", label: "Website and API", icon: Code2 },
  { to: "/partner/insights", label: "Insights", icon: BarChart3 },
  { to: "/partner/clients", label: "Clients", icon: Users },
  { to: "/partner/marketing", label: "Marketing", icon: Megaphone },
  { to: "/partner/posts", label: "Posts", icon: PenSquare },
  { to: "/partner/ads", label: "Ads", icon: Megaphone },
  { to: "/partner/boost", label: "Boost a listing", icon: Rocket },
  { to: "/partner/trust", label: "Trusted badge", icon: BadgeCheck },
  { to: "/partner/offers", label: "Member offers", icon: Gift },
  { to: "/partner/products", label: "Shop products", icon: Package },
  { to: "/partner/plan", label: "Plan & billing", icon: CreditCard },
  { to: "/partner/usage", label: "Calls & texts", icon: Phone },
];

/** Shared provider status for every partner page; refreshes on route change. */
export function useProviderStatus() {
  const [status, setStatus] = useState(undefined);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    providerApi.status().then((s) => alive && setStatus(s)).catch(() => alive && setStatus(null));
    return () => { alive = false; };
  }, [pathname]);
  return status;
}

export function StatusBanner({ status }) {
  if (!status) return null;
  if (status.provider_status === "pending_payment") {
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="partner-banner-pending">
        <ShieldAlert size={18} />
        <span className="flex-1">
          {status.grace_expired
            ? "Your plan isn't active, so your listings are hidden. Choose a plan to bring them back."
            : `Your plan isn't active yet. Choose a plan within ${status.grace_days_left ?? status.grace_days_default} days to keep your listings live.`}
        </span>
        <Link to="/partner/plan"><Button size="sm" className="rounded-full">Choose a plan</Button></Link>
      </div>
    );
  }
  if (status.pending_draft && !status.is_partner) {
    return (
      <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm" data-testid="partner-banner-draft">
        <Rocket size={18} className="text-primary" />
        <span className="flex-1">Your first listing “{status.pending_draft.title}” is saved as a draft. Pick a plan and it goes live.</span>
        <Link to="/partner/start?step=plan"><Button size="sm" className="rounded-full">Finish setup <ArrowRight size={14} className="ml-1" /></Button></Link>
      </div>
    );
  }
  return null;
}

export function PartnerShell() {
  const { user } = useAuth();
  const status = useProviderStatus();
  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--provider)" }}>Partner hub</p>
          <h1 className="mt-1 font-serif-display text-3xl font-bold">{user?.business_name || user?.full_name || "Your business"}</h1>
        </div>
        {status?.plan ? (
          <Link to="/partner/plan" className="rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted" data-testid="partner-plan-chip">
            {status.plan.tier_name || status.plan.tier}
            {status.plan.monthly > 0 ? ` · £${Number(status.plan.monthly).toFixed(2)}/mo` : status.plan.billing_model === "payg" ? " · 2.7% per booking" : ""}
          </Link>
        ) : null}
      </div>
      <StatusBanner status={status} />
      <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
        <nav className="flex gap-1 overflow-x-auto lg:flex-col" aria-label="Partner">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`partner-nav-${n.label.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) => `flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
              >
                <Icon size={17} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="min-w-0"><Outlet context={{ status }} /></div>
      </div>
    </div>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-2 font-serif-display text-3xl font-bold">{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export function Empty({ title, body, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-10 text-center">
      <h3 className="font-bold">{title}</h3>
      {body && <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}
